<template>
	<div class="ms_wrapper" ref="root">
		<button type="button"
			class="ms_trigger"
			:class="{ 'is-active': isActive, 'is-open': isOpen }"
			:aria-expanded="isOpen ? 'true' : 'false'"
			aria-haspopup="listbox"
			@click.stop="toggle">
			<span class="ms_label">{{ placeholder }}</span>
			<span v-if="value.length > 0" class="ms_count_badge">{{ value.length }}</span>
			<ArrowDown class="ms_chevron" :class="{ 'is-rotated': isOpen }" />
		</button>

		<transition name="ms_fade">
			<div v-if="isOpen" class="ms_panel" role="listbox" aria-multiselectable="true" @click.stop>
				<ul class="ms_options">
					<li v-for="opt in options" :key="opt.id"
						class="ms_option"
						:class="{ 'is-checked': isSelected(opt.id) }"
						role="option"
						:aria-selected="isSelected(opt.id) ? 'true' : 'false'"
						@click.stop="toggleOption(opt.id)">
						<span class="ms_check" :class="{ 'is-checked': isSelected(opt.id) }">
							<span class="ms_check_mark" v-if="isSelected(opt.id)"></span>
						</span>
						<span class="ms_option_label">{{ opt.label }}</span>
					</li>
				</ul>
				<button v-if="value.length > 0"
					type="button"
					class="ms_clear_btn"
					@click.stop="clearAll">
					{{ clearLabel }}
				</button>
			</div>
		</transition>
	</div>
</template>

<script>
import ArrowDown from "@/components/svg/ArrowDown.vue";

export default {
	name: "MultiSelectDropdown",

	components: { ArrowDown },

	model: {
		prop: "value",
		event: "input",
	},

	props: {
		value: {
			type: Array,
			default: () => [],
		},
		options: {
			type: Array,
			required: true,
			validator: (arr) => arr.every(o => o && o.id !== undefined && o.label !== undefined),
		},
		placeholder: {
			type: String,
			default: "Seleccionar",
		},
		clearLabel: {
			type: String,
			default: "Limpiar selección",
		},
	},

	data() {
		return { isOpen: false };
	},

	computed: {
		isActive() { return this.value.length > 0; },
	},

	methods: {
		isSelected(id) { return this.value.includes(id); },
		toggle() {
			if (this.isOpen) this.close();
			else this.open();
		},
		open() {
			if (this.isOpen) return;
			this.isOpen = true;
			this.$nextTick(() => {
				document.addEventListener("mousedown", this.handleClickOutside);
			});
		},
		close() {
			if (!this.isOpen) return;
			this.isOpen = false;
			document.removeEventListener("mousedown", this.handleClickOutside);
		},
		toggleOption(id) {
			const idx = this.value.indexOf(id);
			const next = idx === -1
				? [...this.value, id]
				: this.value.filter(v => v !== id);
			this.$emit("input", next);
		},
		clearAll() {
			this.$emit("input", []);
		},
		handleClickOutside(event) {
			const root = this.$refs.root;
			if (root && !root.contains(event.target)) {
				this.close();
			}
		},
	},

	beforeDestroy() {
		document.removeEventListener("mousedown", this.handleClickOutside);
	},
};
</script>

<style scoped lang="scss">
.ms_wrapper {
	position: relative;
	display: inline-flex;
}

.ms_trigger {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 0 12px 0 16px;
	min-height: 34px;
	background: #ffffff;
	border: 1px solid var(--report-border, #e5e7eb);
	border-radius: 8px;
	font-size: 14px;
	font-weight: 500;
	line-height: 1;
	color: #4b5563;
	cursor: pointer;
	transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
	user-select: none;
	box-sizing: border-box;

	&:hover { background: #f9fafb; }

	&.is-active {
		background: var(--report-primary, #00A9AD);
		border-color: var(--report-primary, #00A9AD);
		color: #ffffff;
	}

	&.is-open {
		border-color: var(--report-primary, #00A9AD);
	}
}

.ms_label { line-height: 1; }

.ms_count_badge {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 20px;
	height: 20px;
	padding: 0 6px;
	background: rgba(255, 255, 255, 0.22);
	border-radius: 10px;
	font-size: 12px;
	font-weight: 700;
	line-height: 1;

	.ms_trigger:not(.is-active) & {
		background: var(--report-primary, #00A9AD);
		color: #ffffff;
	}
}

.ms_chevron {
	width: 12px;
	height: 12px;
	transition: transform 0.2s ease;

	.ms_trigger:not(.is-active) & {
		filter: brightness(0) saturate(100%) invert(40%) sepia(0%) saturate(0%) hue-rotate(0deg);
	}

	&.is-rotated { transform: rotate(180deg); }
}

.ms_panel {
	position: absolute;
	top: calc(100% + 6px);
	left: 0;
	min-width: 220px;
	background: #ffffff;
	border: 1px solid var(--report-border, #e5e7eb);
	border-radius: 10px;
	box-shadow: 0 6px 16px rgba(15, 23, 42, 0.08);
	padding: 6px;
	z-index: 4;
}

.ms_options {
	list-style: none;
	margin: 0;
	padding: 0;
}

.ms_option {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 8px 10px;
	border-radius: 6px;
	cursor: pointer;
	color: #4b5563;
	font-size: 14px;
	font-weight: 500;
	transition: background 0.12s ease, color 0.12s ease;
	user-select: none;

	&:hover { background: #f9fafb; }

	&.is-checked { color: var(--report-primary, #00A9AD); }
}

.ms_check {
	flex-shrink: 0;
	width: 16px;
	height: 16px;
	border: 1.5px solid #cbd5e1;
	border-radius: 4px;
	background: #ffffff;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	transition: border-color 0.12s ease, background 0.12s ease;

	&.is-checked {
		border-color: var(--report-primary, #00A9AD);
		background: var(--report-primary, #00A9AD);
	}
}

.ms_check_mark {
	width: 8px;
	height: 4px;
	border-left: 2px solid #ffffff;
	border-bottom: 2px solid #ffffff;
	transform: rotate(-45deg) translate(1px, -1px);
}

.ms_option_label { line-height: 1.2; }

.ms_clear_btn {
	width: 100%;
	margin-top: 4px;
	padding: 8px 10px;
	background: transparent;
	border: none;
	border-top: 1px solid var(--report-border, #e5e7eb);
	border-radius: 0 0 6px 6px;
	font-size: 13px;
	font-weight: 500;
	color: #6b7280;
	cursor: pointer;
	text-align: left;
	transition: color 0.12s ease;

	&:hover { color: var(--report-primary, #00A9AD); }
}

.ms_fade-enter-active,
.ms_fade-leave-active {
	transition: opacity 0.12s ease, transform 0.12s ease;
}

.ms_fade-enter,
.ms_fade-leave-to {
	opacity: 0;
	transform: translateY(-4px);
}
</style>
